import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Compass } from '@/components/icons';
import { EmptyState } from '@/components/ui/empty-state';
import { PageContainer } from '@/components/layout/PageContainer';

/**
 * Canonical 404 page per `sustevo-handbook/brand/ERROR_PAGES.md` § 1.
 *
 * Replaces the pre-2026-05-21 catch-all silent redirect
 * (`{ path: '*', element: <Navigate to="/" replace /> }`) which masked
 * bad URLs by dropping the user on the dashboard with no signal.
 *
 * Renders inside the authenticated AppShell — sidebar stays, content
 * area shows a branded `<EmptyState>` with a Compass icon (lost in
 * nav) and a "Back to home" CTA pointing at the canonical landing
 * route.
 *
 * #1701 — was `<main className="mx-auto max-w-[1200px] px-10 py-12">`
 * which double-padded against the AppShell's `<main>` (already
 * `px-4 py-6 lg:px-8`). `<PageContainer size="wide" paddingX="none">`
 * lets the shell own the horizontal gutter; `paddingY="loose"` keeps
 * the generous top/bottom breathing room appropriate for an
 * empty-state screen.
 */
export function NotFoundPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div data-testid="not-found-page">
      <PageContainer size="wide" paddingX="none" paddingY="loose">
        <EmptyState
          icon={<Compass className="h-10 w-10" aria-hidden="true" />}
          title={t('errors.notFound.title', {
            defaultValue: 'Page not found',
          })}
          description={t('errors.notFound.body', {
            defaultValue:
              "The URL you followed doesn't match anything we know about. It may have been moved or never existed.",
          })}
          action={
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
            >
              {t('errors.notFound.backHome', { defaultValue: 'Back to home' })}
            </Link>
          }
        />
      </PageContainer>
    </div>
  );
}
